import { Autocomplete, TextField } from "@mui/material";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import { EmployeeEntity } from "tng-address-collection-shared/types/entities.ts";
import { unknownEmployee } from "tng-address-collection-shared/unknownEmployee.ts";
import { useGetEmployees } from "../hooks/useGetEmployees.ts";
import { useTranslator } from "../i18n/translator.ts";
import { describedBy } from "./describedBy.ts";

export const EmployeesMultiSelectionFormField = <
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
>({
  control,
  name,
  label,
  options,
  ariaDescribedBy,
}: {
  control: Control<TFieldValues>;
  name: TName;
  label: string;
  options?: EmployeeEntity[];
  ariaDescribedBy?: string;
}) => {
  const { data: employees, isFetching } = useGetEmployees();
  const translator = useTranslator();

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <Autocomplete
          multiple
          options={options ?? employees ?? []}
          noOptionsText={translator(isFetching ? "loading" : "noOptions")}
          filterSelectedOptions
          getOptionKey={(option) => option.uidNumber}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) =>
            option.uidNumber === value.uidNumber
          }
          value={
            (field.value as number[] | undefined)?.map(
              (uidNumber) =>
                employees?.find((x) => x.uidNumber === uidNumber) ??
                unknownEmployee(uidNumber),
            ) ?? []
          }
          onChange={(_, value) => field.onChange(value.map((x) => x.uidNumber))}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              variant="outlined"
              fullWidth
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
                htmlInput: {
                  ...params.inputProps,
                  "aria-describedby": describedBy(
                    ariaDescribedBy,
                    !!fieldState.error?.message && `${params.id}-error`,
                  ),
                },
                formHelperText: { id: `${params.id}-error` },
              }}
            />
          )}
        />
      )}
    />
  );
};
